import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';
import { ClaimService } from 'src/app/services/claim.service';
import { CustomerService } from 'src/app/services/customer.service';
import { skip, Subscription } from 'rxjs';

interface ClaimItemRow {
  id: number;
  selected: boolean;
  materialNumber: string;
  description: string;
  unitPrice: string;
  deliveryNumber: string;
  deliveryLineNumber: string;
  deliveryQty: number;
  remainingQty: number;
  claimQty: number;
  amount: string;
  // order mode extra fields
  orderNo?: string;
  orderItemNo?: string;
  orderTotalAmount?: string;
  orderQuantity?: number;
  // invoice mode extra fields
  invoiceNo?: string;
  invoiceItemNo?: string;
  dnTotalValueWithTax?: string;
  dnQuantity?: number;
  priceDifference?: string;
  raw: any;
}

interface ClaimCartItem {
  rowId: number;
  materialNumber: string;
  invoiceAmount: string;
  orderLineQty: number;
  deliveryNumber: string;
  deliveryLineNumber: string;
  deliveryQty: number;
  submittableQty: number;
  claimQty: number;
  claimAmount: string;
  receiverNumber: string;
  returnOrderNumber: string;
  // order mode extra fields
  orderNo?: string;
  orderItemNo?: string;
  orderTotalAmount?: string;
  orderQuantity?: number;
  // invoice mode extra fields
  invoiceNo?: string;
  invoiceItemNo?: string;
  dnTotalValueWithTax?: string;
  dnQuantity?: number;
  priceDifference?: string;
  raw: any;
}

@Component({
  selector: 'create-claim',
  templateUrl: './create-claim.component.html',
  styleUrls: ['./create-claim.component.less'],
})
export class CreateClaimComponent implements OnInit, OnDestroy {
  basicForm!: FormGroup;
  orderForm!: FormGroup;
  otherForm!: FormGroup;

  private customerChangeSub?: Subscription;

  // Edit mode — populated when navigated from the overview "edit" action.
  isEditMode = false;
  editingClaimId: string | null = null;
  editingClaimNumber: string | null = null;
  editingCustomerReferenceId: string | null = null;
  editingClaimStatus: string | null = null;
  editingClaimStatusName: string | null = null;
  pageTitleKey = 'CLAIM_CREATE_PAGE_TITLE';
  detailLoading = false;
  customerDocTypeLoading = false;
  metaLoading = false;
  private suppressMetaCascade = false;
  private metaCascadeReqSeq = 0;

  // 参考类型 —— 由 GET /customerDocType/list 动态填充
  referenceTypeOptions: { labelKey: string; value: string }[] = [];

  private static readonly REFERENCE_TYPE_MAP: Record<
    string,
    { labelKey: string; value: string }
  > = {
    normal: { labelKey: 'CLAIM_STANDARD_BINDING_ORDER', value: 'normal' },
    consignment: { labelKey: 'CLAIM_CONSIGNMENT_ORDER', value: 'consignment' },
  };

  // 索赔原因 / 索赔分类 / 索赔方式 —— 全部来自 /claimMeta/cascade，
  // option.value 为 nameEn（业务键，与 ClaimDetailVO 的 claimType /
  // claimCategory / ruleLevel2 同源），label 由后端按 Accept-Language 预解析。
  reasonOptions: { label: string; value: string }[] = [];
  categoryOptions: { label: string; value: string; parentName?: string }[] = [];
  schemeOptions: { label: string; value: string }[] = [];

  // 索引号
  indexOptions = [
    { label: '118000008-001', value: 'idx-001' },
    { label: '118000008-002', value: 'idx-002' },
  ];

  // 是否自行寄回
  selfReturnOptions = [
    { labelKey: 'CLAIM_CREATE_RETURN_BY_SELF', value: 'yes' },
    { labelKey: 'CLAIM_CREATE_BOSCH_PICKUP', value: 'no' },
  ];

  warehouseInfo = {
    address: '江苏省苏州市太仓市港口开发区金港路2号博世亚太物流中心A1仓库',
    contact: '侯昊松',
    phone: '13526207938',
  };

  customerAddressOptions = [
    { label: '上海市浦东新区博世大厦', value: 'addr-1' },
    { label: '北京市朝阳区博世中心', value: 'addr-2' },
  ];

  customerContactOptions = [
    { label: '张先生', value: 'contact-1' },
    { label: '李女士', value: 'contact-2' },
  ];

  customerPhoneOptions = [
    { label: '13800138000', value: 'phone-1' },
    { label: '13900139000', value: 'phone-2' },
  ];

  // Column widths tuned to fit the 70.75rem (~1132px) container. The visual
  // proportions follow Figma node 170:5100 (total ~1280px) but compressed.
  columns = [
    { key: 'select', name: '', width: 40 },
    { key: 'materialNumber', name: '博世物料编号', width: 110 },
    { key: 'description', name: '描述', width: 140 },
    { key: 'unitPrice', name: '单价', width: 100 },
    { key: 'deliveryNumber', name: '提单号', width: 90 },
    { key: 'deliveryLineNumber', name: '提单行号', width: 80 },
    { key: 'deliveryQty', name: '提单行数量', width: 88 },
    { key: 'remainingQty', name: '剩余可索赔数量', width: 110 },
    { key: 'claimQty', name: '索赔数量', width: 130 },
    { key: 'amount', name: '索赔行金额（不含税）', width: 130 },
    { key: 'action', name: '一键添加', width: 84 },
  ];

  dataSource: ClaimItemRow[] = [];
  itemsLoading = false;

  pagination = {
    total: 1,
    pageIndex: 1,
    pageSize: 10,
  };

  // 跨页选中的临时变量，翻页时保存当前页选中行，点击一键添加时合并加入购物车
  private pendingSelectedRows: ClaimItemRow[] = [];

  // 已上传到 BSP 的附件清单。attachmentId 在提交 Claim 时随
  // saveDraft / submit 一起回传给后端。
  attachments: {
    attachmentId: string;
    fileName: string;
    size?: number;
    uploading?: boolean;
  }[] = [];
  attachmentUploading = false;

  // History dropdowns for 提单号 / 博世物料编号 — stored in browser cookies
  private readonly DELIVERY_HISTORY_KEY = 'claim_delivery_number_history';
  private readonly INVOICE_HISTORY_KEY = 'claim_invoice_number_history';
  private readonly MATERIAL_HISTORY_KEY = 'claim_material_number_history';
  deliveryNumberHistory: string[] = [];
  invoiceNumberHistory: string[] = [];
  materialNumberHistory: string[] = [];
  activeHistoryField: 'deliveryNumber' | 'materialNumber' | null = null;

  // Claim cart — Figma node 170:4391 (icon) / 170:6346 (popup)
  cartItems: ClaimCartItem[] = [];
  cartOpen = false;

  // Captured at construction time because router state is only available
  // synchronously during navigation; ngOnInit reads it after the router has
  // already cleared the current navigation reference.
  private readonly navState: { claim?: any } | null;

  constructor(
    private fb: FormBuilder,
    private elementRef: ElementRef,
    private router: Router,
    private route: ActivatedRoute,
    private claimService: ClaimService,
    private message: NzMessageService,
    private customerService: CustomerService,
    private translate: TranslateService,
  ) {
    const nav = this.router.getCurrentNavigation();
    this.navState = (nav?.extras?.state as { claim?: any }) ?? null;
  }

  ngOnInit(): void {
    this.basicForm = this.fb.group({
      referenceType: ['normal'],
      reason: [null],
      category: [null],
      indexNumber: [null],
      scheme: [null],
      selfReturn: ['no'],
      customerAddress: [null],
      customerContact: [null],
      customerPhone: [null],
    });

    this.orderForm = this.fb.group({
      deliveryNumber: [''],
      materialNumber: [''],
      orderNumber: [''],
      dnCreateDateRange: [null],
    });

    this.otherForm = this.fb.group({
      remark: [''],
    });

    this.loadDeliveryNumberHistory();
    this.loadCustomerDocType();
    this.bindMetaCascade();

    // 切换 参考类型 时清空表格 + 购物车 + 基本信息部分字段，避免上一模式的脏数据。
    this.basicForm
      .get('referenceType')!
      .valueChanges.subscribe((val) => {
        this.suppressMetaCascade = true;
        this.basicForm.patchValue({
          reason: null,
          category: null,
          scheme: null,
          customerAddress: null,
          customerContact: null,
          customerPhone: null,
        }, { emitEvent: false });
        this.suppressMetaCascade = false;
        // Update orderType: 寄售 → order, 非寄售 → invoice
        this.orderType = val === 'consignment' ? 'order' : 'invoice';
        this.dataSource = [];
        this.cartItems = [];
        this.pagination.pageIndex = 1;
        this.pagination.total = 0;
        this.refreshMetaCascade();
      });

    this.editingClaimId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.editingClaimId;
    if (this.isEditMode) {
      this.pageTitleKey = 'CLAIM_CREATE_PAGE_TITLE_EDIT';
      // Use the row passed via router state for an instant first paint, then
      // overwrite with the authoritative backend detail (covers page refresh
      // where state is gone and ensures we always show the latest data).
      this.loadClaimForEdit(this.editingClaimId!, this.navState?.claim);
      this.fetchClaimDetail(this.editingClaimId!);
    }

    // 切换客户时跳转回索赔概览页
    this.customerChangeSub = this.customerService.selectedCustomer$
      .pipe(skip(1))
      .subscribe(() => {
        this.handleCancel();
      });
  }

  ngOnDestroy(): void {
    this.customerChangeSub?.unsubscribe();
  }

  /**
   * 根据 `GET /customerDocType/list` 填充「订单类型」单选项并设置默认选中值。
   *
   * 展示规则：后端返回几个就展示几个（如 `["Normal","Consignment"]` 展示两项，
   * 仅返回其一则只展示该项）。
   *
   * 默认选中规则（仅创建模式）：
   *   - 选中后端返回列表中的第一项（顺序与后端一致）
   *   - 空集合 / 失败 → 回退为标准/空运订单
   *
   * 编辑模式下选项仍由本接口决定，但选中值以 `applyClaimDetail` 为准。
   */
  // orderType derived from referenceType after customerDocType loads
  orderType: string = 'order';

  private buildReferenceTypeOptions(
    list: string[],
  ): { labelKey: string; value: string }[] {
    const seen = new Set<string>();
    const options: { labelKey: string; value: string }[] = [];
    for (const item of list) {
      const key = (item ?? '').toString().trim().toLowerCase();
      const option = CreateClaimComponent.REFERENCE_TYPE_MAP[key];
      if (option && !seen.has(key)) {
        seen.add(key);
        options.push(option);
      }
    }
    return options;
  }

  private resolveDefaultReferenceType(
    options: { labelKey: string; value: string }[],
  ): string {
    return options[0]?.value ?? 'normal';
  }

  private applyReferenceTypeOptions(
    options: { labelKey: string; value: string }[],
  ): void {
    this.referenceTypeOptions =
      options.length > 0
        ? options
        : [CreateClaimComponent.REFERENCE_TYPE_MAP['normal']];

    if (!this.isEditMode) {
      const referenceType = this.resolveDefaultReferenceType(
        this.referenceTypeOptions,
      );
      this.basicForm.patchValue({ referenceType }, { emitEvent: false });
      this.orderType = referenceType === 'consignment' ? 'order' : 'invoice';
      this.refreshMetaCascade();
    }
  }

  private loadCustomerDocType(): void {
    this.customerDocTypeLoading = true;
    this.claimService.apiCustomerDocTypeList().subscribe({
      next: (result: any) => {
        const list: string[] = Array.isArray(result?.rtnData)
          ? result.rtnData
          : [];
        this.applyReferenceTypeOptions(this.buildReferenceTypeOptions(list));
        this.customerDocTypeLoading = false;
      },
      error: () => {
        this.applyReferenceTypeOptions([]);
        this.customerDocTypeLoading = false;
      },
    });
  }

  /**
   * 索赔原因 / 索赔分类 / 索赔方式 三个下拉的级联联动。后端 `/claimMeta/cascade`
   * 是单向收敛（上游不被下游约束），实现策略与 `claim-overview` 完全一致：
   *   - 选 reason   → 收紧 category + scheme
   *   - 选 category → 收紧 scheme（reason 仍是全集）
   *   - 选 scheme   → 不会收敛上游
   *
   * 表单控件名 → 后端字段：
   *   reason   → claimType   (claimTypeName)
   *   category → claimCategory (claimCategoryName)
   *   scheme   → claimSolution (ruleLevel2)
   */
  private bindMetaCascade(): void {
    // 索赔原因变更时，先清除索赔分类和索赔方式，再触发级联查询
    this.basicForm.get('reason')!.valueChanges.subscribe(() => {
      if (this.suppressMetaCascade) {
        return;
      }
      this.basicForm.patchValue({
        category: null,
        scheme: null,
      }, { emitEvent: false });
      this.refreshMetaCascade();
    });

    // 索赔分类 / 索赔方式 变更时直接触发级联查询
    (['category', 'scheme'] as const).forEach(controlName => {
      this.basicForm.get(controlName)!.valueChanges.subscribe(() => {
        if (this.suppressMetaCascade) {
          return;
        }
        this.refreshMetaCascade();
      });
    });
  }

  private refreshMetaCascade(): void {
    const reason = this.basicForm?.get('reason')?.value ?? null;
    const category = this.basicForm?.get('category')?.value ?? null;
    const scheme = this.basicForm?.get('scheme')?.value ?? null;

    const reqSeq = ++this.metaCascadeReqSeq;
    this.metaLoading = true;
    this.claimService
      .apiClaimMetaCascade({
        claimTypeName: reason,
        claimCategoryName: category,
        claimSolutionName: scheme,
        referType: this.orderType,
      })
      .subscribe({
        next: (result: any) => {
          if (reqSeq !== this.metaCascadeReqSeq) {
            return;
          }
          const data = result?.rtnData ?? {};
          const claimTypes: { value: string; name: string }[] =
            data.claimTypes ?? [];
          const claimCategories: {
            value: string;
            name: string;
            parentName?: string;
          }[] = data.claimCategories ?? [];
          const claimSolutions: { value: string; name: string }[] =
            data.claimSolutions ?? [];

          this.reasonOptions = claimTypes.map(item => ({
            label: item.name,
            value: item.value,
          }));
          this.categoryOptions = claimCategories.map(item => ({
            label: item.name,
            value: item.value,
            parentName: item.parentName,
          }));
          this.schemeOptions = claimSolutions.map(item => ({
            label: item.name,
            value: item.value,
          }));

          // 编辑回填时后端可能返回 name/nameEn 任一形态，这里做一次
          // label/value 归一化。
          this.reconcileControlValue('reason', this.reasonOptions);
          this.reconcileControlValue('category', this.categoryOptions);
          this.reconcileControlValue('scheme', this.schemeOptions);

          // 当前选中的值不在级联返回的选项中时，重置该字段
          this.clearStaleSelection('reason', this.reasonOptions);
          this.clearStaleSelection('category', this.categoryOptions);
          this.clearStaleSelection('scheme', this.schemeOptions);

          // 索赔分类/索赔方式 只有唯一选项时自动选中（emitEvent:false 避免循环触发级联）
          this.autoSelectSingleOption('category', this.categoryOptions);
          this.autoSelectSingleOption('scheme', this.schemeOptions);

          this.metaLoading = false;
        },
        error: () => {
          if (reqSeq === this.metaCascadeReqSeq) {
            this.metaLoading = false;
          }
          this.message.error(this.translate.instant('CLAIM_MSG_META_FAIL'));
        },
      });
  }

  /**
   * 当下拉选项只有唯一一个且当前未选中任何值时，自动选中该选项。
   * 典型场景：选定索赔原因后，后端级联返回的索赔分类或索赔方式仅有一个选项。
   */
  private autoSelectSingleOption(
    controlName: 'category' | 'scheme',
    options: { value: string }[],
  ): void {
    const control = this.basicForm.get(controlName);
    if (!control) {
      return;
    }
    const current = control.value;
    if (current !== null && current !== undefined && `${current}`.trim() !== '') {
      return;
    }
    if (options.length === 1) {
      control.setValue(options[0].value, { emitEvent: false });
    }
  }

  /**
   * If the current value of `controlName` is no longer present in `options`
   * (because the cascade narrowed the list), clear it silently so the user
   * isn't left with an invalid selection. `emitEvent: false` prevents an
   * infinite cascade-refresh loop.
   */
  private clearStaleSelection(
    controlName: 'reason' | 'category' | 'scheme',
    options: { value: string }[],
  ): void {
    const control = this.basicForm.get(controlName);
    const value = control?.value;
    if (value && !options.some(opt => opt.value === value)) {
      control!.setValue(null, { emitEvent: false });
    }
  }

  /**
   * Fetches the authoritative `ClaimDetailVO` from `GET /claim/{id}` and
   * patches the form. Only fields that have a corresponding control in the
   * basic / order / other forms are mapped — table rows / attachments will
   * be wired separately when the create-claim form switches off mocks.
   */
  private fetchClaimDetail(id: string): void {
    this.detailLoading = true;
    this.claimService.apiClaimDetail(id).subscribe({
      next: (result: any) => {
        const vo = result?.rtnData;
        this.detailLoading = false;
        if (!vo) {
          return;
        }
        this.editingClaimNumber = vo.claimNumber ?? null;
        this.editingCustomerReferenceId = vo.customerReferenceId ?? null;
        this.editingClaimStatus = vo.claimStatus ?? null;
        this.editingClaimStatusName = vo.claimStatusName ?? null;
        this.applyClaimDetail(vo);
      },
      error: () => {
        this.detailLoading = false;
        this.message.error(this.translate.instant('CLAIM_MSG_DETAIL_FAIL'));
      },
    });
  }

  /**
   * Maps `ClaimDetailVO` to the basic / other form controls. Uses the
   * backend nameEn values directly as option values — make sure the
   * dropdowns are bound to the same source (cascade/meta APIs) for the
   * patch to render correctly.
   */
  private applyClaimDetail(vo: any): void {
    const ruleLevel1 = (vo.ruleLevel1 ?? '').toString().toLowerCase();
    const customerAddress = this.pickFirstString(vo, [
      'contactAddress',
      'customerAddress',
      'customerPickUpAddress',
    ]);
    const customerContact = this.pickFirstString(vo, [
      'contactName',
      'customerContact',
      'customerPickupContact',
    ]);
    const customerPhone = this.pickFirstString(vo, [
      'contactPhone',
      'customerPhone',
      'customerPickupPhone',
    ]);
    const customerReferenceId =
      vo.customerReferenceId !== null && vo.customerReferenceId !== undefined
        ? `${vo.customerReferenceId}`
        : null;

    const indexOptionValue = customerReferenceId
      ? this.ensureIndexOption(customerReferenceId)
      : null;
    if (customerAddress) {
      this.ensureSimpleOption(this.customerAddressOptions, customerAddress);
    }
    if (customerContact) {
      this.ensureSimpleOption(this.customerContactOptions, customerContact);
    }
    if (customerPhone) {
      this.ensureSimpleOption(this.customerPhoneOptions, customerPhone);
    }

    this.suppressMetaCascade = true;
    this.basicForm.patchValue(
      {
        referenceType: ruleLevel1 === 'consignment' ? 'consignment' : 'normal',
        reason: this.resolveSelectValue(vo.claimType, this.reasonOptions),
        category: this.resolveSelectValue(vo.claimCategory, this.categoryOptions),
        indexNumber: indexOptionValue,
        scheme: this.resolveSelectValue(vo.ruleLevel2, this.schemeOptions),
        selfReturn: vo.selfReturn === 1 ? 'yes' : 'no',
        customerAddress: customerAddress || null,
        customerContact: customerContact || null,
        customerPhone: customerPhone || null,
      },
      { emitEvent: false },
    );
    this.suppressMetaCascade = false;
    // Sync orderType with the patched referenceType so refreshMetaCascade uses
    // the correct value (patchValue with emitEvent:false skips valueChanges).
    this.orderType = ruleLevel1 === 'consignment' ? 'order' : 'invoice';
    if (this.otherForm) {
      this.otherForm.patchValue({ remark: vo.comments ?? '' });
    }

    if (Array.isArray(vo.attachments)) {
      this.attachments = vo.attachments
        .filter((att: any) => !!att?.attachmentId)
        .map((att: any) => ({
          attachmentId: `${att.attachmentId}`,
          fileName: att.fileName ?? att.name ?? `${att.attachmentId}`,
        }));
    }

    this.hydrateCartFromReferences(vo.references ?? []);

    // 编辑态回填后只触发一次级联刷新，避免 patchValue 触发多次。
    this.refreshMetaCascade();

    // 编辑态：从购物车数据构建主表格行，回显已保存的索赔数据。
    this.hydrateDataSourceFromCart();
    this.pagination.pageIndex = 1;
    this.pagination.total = this.dataSource.length;
  }

  private resolveSelectValue(
    raw: unknown,
    options: { label: string; value: string }[],
  ): string | null {
    if (raw === null || raw === undefined || `${raw}`.trim() === '') {
      return null;
    }
    const value = `${raw}`;
    const matched = options.find(
      opt => opt.value === value || opt.label === value,
    );
    return matched ? matched.value : value;
  }

  private reconcileControlValue(
    controlName: 'reason' | 'category' | 'scheme',
    options: { label: string; value: string }[],
  ): void {
    const control = this.basicForm.get(controlName);
    const current = control?.value;
    if (current === null || current === undefined || `${current}`.trim() === '') {
      return;
    }
    const value = `${current}`;
    const matched = options.find(
      opt => opt.value === value || opt.label === value,
    );
    if (matched && matched.value !== value) {
      control!.setValue(matched.value, { emitEvent: false });
    }
  }

  private ensureCurrentSelectionOption(
    controlName: 'reason' | 'category' | 'scheme',
    options: { label: string; value: string }[],
  ): void {
    const value = this.basicForm.get(controlName)?.value;
    if (value === null || value === undefined || `${value}`.trim() === '') {
      return;
    }
    const text = `${value}`;
    if (options.some(opt => opt.value === text)) {
      return;
    }
    options.push({ label: text, value: text });
  }

  /**
   * Populates the form when navigated from the overview "edit" action.
   * Prefers the row data passed via router state (no backend round-trip).
   * Maps localized display labels back to option values.
   */
  private loadClaimForEdit(claimNumber: string, claim: any | undefined): void {
    if (!claim) {
      // No state available (e.g. user refreshed the page). Leave the form blank
      // for now — once a backend exists, fetch by claimNumber here.
      return;
    }

    if (claim) {
      this.editingCustomerReferenceId = claim.indexNumber ?? null;
      this.editingClaimStatus = claim.status ?? null;
      this.editingClaimStatusName = claim.statusName ?? null;
    }

    // reason / category / scheme 不再从 state 推断 —— 它们的下拉项来自
    // /claimMeta/cascade（异步），且 fetchClaimDetail 会用后端权威 nameEn
    // 直接 patch。这里只需要把 indexNumber 这种「下拉还在 mock」的字段做个
    // 即时回显，等对应接口接入后即可移除。
    if (claim.indexNumber) {
      const existing = this.indexOptions.find(o => o.label === claim.indexNumber);
      const value = existing ? existing.value : this.ensureIndexOption(claim.indexNumber);
      this.basicForm.patchValue({ indexNumber: value });
    }
  }

  /** Make sure an index value coming from the row is selectable in the dropdown. */
  private ensureIndexOption(label: string): string {
    const existing = this.indexOptions.find((o) => o.label === label);
    if (existing) {
      return existing.value;
    }
    const value = `idx-${label}`;
    this.indexOptions = [...this.indexOptions, { label, value }];
    return value;
  }

  // ---- Status chip helpers ----

  getStatusLabel(status: string): string {
    switch (status) {
      case 'DRAFT':
        return '草稿';
      case 'IN_PROGRESS':
        return '进行中';
      case 'APPROVED':
        return '已受理';
      case 'COMPLETED':
        return '已完成';
      case 'REJECTED':
        return '已拒绝';
      default:
        return status;
    }
  }

  getStatusColor(status: string): { color: string; bg: string } {
    switch (status) {
      case 'DRAFT':
        return { color: '#71767C', bg: '#F2F3F4' };
      case 'IN_PROGRESS':
        return { color: '#FF8726', bg: '#FFF6EE' };
      case 'APPROVED':
        return { color: '#009C5F', bg: '#E6F6EF' };
      case 'COMPLETED':
        return { color: '#18837E', bg: '#F1F8F7' };
      case 'REJECTED':
        return { color: '#ED0007', bg: '#FEEDED' };
      case 'CANCEL':
        return { color: '#007BC0', bg: '#E6F2F9' };
      default:
        return { color: '#A0A0A0', bg: '#F2F3F4' };
    }
  }

  private ensureSimpleOption(
    options: { label: string; value: string }[],
    value: string,
  ): void {
    if (options.some((o) => o.value === value)) {
      return;
    }
    options.push({ label: value, value });
  }

  isRowClaimable(row: ClaimItemRow): boolean {
    return row.remainingQty > 0;
  }

  isCartItemClaimable(item: ClaimCartItem): boolean {
    return item.submittableQty > 0;
  }

  get allSelected(): boolean {
    const claimable = this.dataSource.filter(r => this.isRowClaimable(r));
    return claimable.length > 0 && claimable.every(r => r.selected);
  }

  get someSelected(): boolean {
    const claimable = this.dataSource.filter(r => this.isRowClaimable(r));
    return claimable.some(r => r.selected) && !this.allSelected;
  }

  get selectedCount(): number {
    return this.dataSource.filter((r) => r.selected).length;
  }

  toggleAll(value: boolean): void {
    this.dataSource.forEach((r) => {
      if (this.isRowClaimable(r)) {
        r.selected = value;
      }
    });
  }

  toggleRow(row: ClaimItemRow, value: boolean): void {
    if (!this.isRowClaimable(row)) {
      row.selected = false;
      return;
    }
    row.selected = value;
  }

  decreaseQty(row: ClaimItemRow): void {
    if (row.claimQty > 0) {
      row.claimQty -= 1;
      this.recalcRowAmount(row);
      this.autoSelectRowOnQtyChange(row);
    }
  }

  increaseQty(row: ClaimItemRow): void {
    if (row.claimQty < row.remainingQty) {
      row.claimQty += 1;
      this.recalcRowAmount(row);
      this.autoSelectRowOnQtyChange(row);
    }
  }

  handleQtyInput(row: ClaimItemRow, value: any): void {
    const num = Number(value);
    if (!isNaN(num)) {
      row.claimQty = Math.max(1, Math.min(num, row.remainingQty));
      this.recalcRowAmount(row);
      this.autoSelectRowOnQtyChange(row);
    }
  }

  private autoSelectRowOnQtyChange(row: ClaimItemRow): void {
    if (!row.selected) {
      row.selected = true;
    }
  }

  /**
   * 索赔行金额计算：
   * - 价格差异模式：（发票行总金额（不含税）/ 发票行数量 - 正确的销售单价（不含税））* 索赔数量
   * - 发票模式：发票行总金额（含税）* 索赔数量 / 发票行数量(billedQty)
   * - 订单模式：订单行总金额（含税）* 索赔数量 / 订单行数量(orderQuantity)
   */
  private recalcRowAmount(row: ClaimItemRow): void {
    let amount = 0;
    if (this.isPriceDifferenceMode) {
      const priceDiff = Number(row.priceDifference) || 0;
      if (this.isInvoiceMode) {
        const totalAmount = this.extractMoneyNumber(row.dnTotalValueWithTax);
        const dnQuantity = row.dnQuantity ?? 0;
        amount = dnQuantity > 0 ? (totalAmount / dnQuantity - priceDiff) * row.claimQty : 0;
      } else {
        const totalAmount = this.extractMoneyNumber(row.orderTotalAmount);
        const orderQty = row.orderQuantity ?? 0;
        amount = orderQty > 0 ? (totalAmount / orderQty - priceDiff) * row.claimQty : 0;
      }
    } else if (this.isInvoiceMode) {
      const totalAmount = this.extractMoneyNumber(row.dnTotalValueWithTax);
      const dnQuantity = row.dnQuantity ?? 0;
      amount = dnQuantity > 0 ? totalAmount * row.claimQty / dnQuantity : 0;
    } else {
      const totalAmount = this.extractMoneyNumber(row.orderTotalAmount);
      const orderQty = row.orderQuantity ?? 0;
      amount = orderQty > 0 ? totalAmount * row.claimQty / orderQty : 0;
    }
    const currency = this.currencyFromMoney(row.unitPrice) ?? undefined;
    row.amount = this.formatMoney(amount, currency);
  }

  showHistory(field: 'deliveryNumber' | 'materialNumber'): void {
    this.activeHistoryField = field;
  }

  hideHistory(): void {
    this.activeHistoryField = null;
  }

  selectHistoryItem(field: 'deliveryNumber' | 'materialNumber', value: string): void {
    this.orderForm.patchValue({ [field]: value });
    this.activeHistoryField = null;
  }

  private loadDeliveryNumberHistory(): void {
    try {
      const dn = this.getCookie(this.DELIVERY_HISTORY_KEY);
      this.deliveryNumberHistory = dn ? JSON.parse(dn) : [];
      const inv = this.getCookie(this.INVOICE_HISTORY_KEY);
      this.invoiceNumberHistory = inv ? JSON.parse(inv) : [];
      const mat = this.getCookie(this.MATERIAL_HISTORY_KEY);
      this.materialNumberHistory = mat ? JSON.parse(mat) : [];
    } catch {
      this.deliveryNumberHistory = [];
      this.invoiceNumberHistory = [];
      this.materialNumberHistory = [];
    }
  }

  private saveDeliveryNumberHistory(value: string): void {
    const key = this.isInvoiceMode ? this.INVOICE_HISTORY_KEY : this.DELIVERY_HISTORY_KEY;
    const history = this.isInvoiceMode ? this.invoiceNumberHistory : this.deliveryNumberHistory;
    const updated = [value, ...history.filter(v => v !== value)].slice(0, 6);
    if (this.isInvoiceMode) {
      this.invoiceNumberHistory = updated;
    } else {
      this.deliveryNumberHistory = updated;
    }
    this.setCookie(key, JSON.stringify(updated), 365);
  }

  private saveMaterialNumberHistory(value: string): void {
    const updated = [value, ...this.materialNumberHistory.filter(v => v !== value)].slice(0, 6);
    this.materialNumberHistory = updated;
    this.setCookie(this.MATERIAL_HISTORY_KEY, JSON.stringify(updated), 365);
  }

  private getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.activeHistoryField) {
      return;
    }
    const target = event.target as HTMLElement;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.activeHistoryField = null;
      return;
    }
    if (!target.closest('.history-field')) {
      this.activeHistoryField = null;
    }
  }

  /**
   * 非寄售(`normal`) → 发票模式（`/claimInvoice/search`）
   *  寄售(`consignment`) → 订单模式（`/claimOrder/search`）
   */
  get isInvoiceMode(): boolean {
    return this.basicForm?.get('referenceType')?.value === 'normal';
  }

  get currentDeliveryHistory(): string[] {
    return this.isInvoiceMode ? this.invoiceNumberHistory : this.deliveryNumberHistory;
  }

  handleClearOrderFilter(): void {
    this.orderForm.reset({
      deliveryNumber: '',
      materialNumber: '',
      orderNumber: '',
      dnCreateDateRange: null,
    });
    this.dataSource = [];
    this.pendingSelectedRows = [];
    this.pagination = { ...this.pagination, total: 0, pageIndex: 1 };
  }

  /**
   * Loads the items table. Branches on the 参考类型 radio: 发票模式 calls
   * `/claimInvoice/search`, 订单模式 calls `/claimOrder/search`. Backend
   * derives org / channel / division / customer from the request header
   * context, so the frontend only sends the user-entered filters + paging.
   */
  handleSearchOrder(newSearch = false): void {
    if (this.itemsLoading) {
      return;
    }
    // 新搜索时清空跨页选中临时变量
    if (newSearch) {
      this.pendingSelectedRows = [];
      this.pagination.pageIndex = 1;
    }
    const deliveryNumberVal = (this.orderForm.get('deliveryNumber')?.value ?? '').trim();
    if (deliveryNumberVal) {
      this.saveDeliveryNumberHistory(deliveryNumberVal);
    }
    const materialNumberVal = (this.orderForm.get('materialNumber')?.value ?? '').trim();
    if (materialNumberVal) {
      this.saveMaterialNumberHistory(materialNumberVal);
    }
    this.itemsLoading = true;
    if (this.isInvoiceMode) {
      this.searchInvoiceItems();
    } else {
      this.searchOrderItems();
    }
  }

  private searchOrderItems(): void {
    const v = this.orderForm.value;
    const range = v.dnCreateDateRange;
    const dnCreateDateFrom = range?.[0] ? this.formatDateParam(range[0]) : undefined;
    const dnCreateDateTo = range?.[1] ? this.formatDateParam(range[1]) : undefined;
    const param = this.dropEmpty({
      dnNo: v.deliveryNumber,
      materialCode: v.materialNumber,
      orderNo: v.orderNumber,
      dnCreateDateFrom,
      dnCreateDateTo,
    });
    const req = {
      param,
      current: this.pagination.pageIndex,
      size: this.pagination.pageSize,
    };
    this.claimService.apiClaimOrderSearch(req).subscribe({
      next: (result: any) => {
        this.itemsLoading = false;
        const data = result?.rtnData ?? {};
        const rows = (data.rows ?? []) as any[];
        this.dataSource = rows.map((vo, idx) => this.mapOrderRow(vo, idx));
        this.applyCartSelectionToDataSource();
        this.pagination.total = data.total ?? 0;
      },
      error: () => {
        this.itemsLoading = false;
        this.message.error(this.translate.instant('CLAIM_CREATE_ORDER_ITEMS_FAIL'));
      },
    });
  }

  private searchInvoiceItems(): void {
    const v = this.orderForm.value;
    const range = v.dnCreateDateRange;
    const dnCreateDateFrom = range?.[0] ? this.formatDateParam(range[0]) : undefined;
    const dnCreateDateTo = range?.[1] ? this.formatDateParam(range[1]) : undefined;
    const param = this.dropEmpty({
      referenceNo: v.deliveryNumber, // 提单号
      billingNo: v.orderNumber, // 发票号
      materialNumber: v.materialNumber,
      dnCreateDateFrom,
      dnCreateDateTo,
      onlyAvailable: true,
    });
    const req = {
      param,
      current: this.pagination.pageIndex,
      size: this.pagination.pageSize,
    };
    this.claimService.apiClaimInvoiceSearch(req).subscribe({
      next: (result: any) => {
        this.itemsLoading = false;
        const data = result?.rtnData ?? {};
        const rows = (data.rows ?? []) as any[];
        this.dataSource = rows.map((vo, idx) => this.mapInvoiceRow(vo, idx));
        this.applyCartSelectionToDataSource();
        this.pagination.total = data.total ?? 0;
      },
      error: () => {
        this.itemsLoading = false;
        this.message.error(this.translate.instant('CLAIM_CREATE_INVOICE_ITEMS_FAIL'));
      },
    });
  }

  /** ClaimOrderItemVO → ClaimItemRow（订单/DN 模式）。 */
  private mapOrderRow(vo: any, idx: number): ClaimItemRow {
    const unitPriceNum = Number(vo.unitPriceWithTax ?? vo.unitPrice ?? 0);
    const remaining = Number(vo.availableQuantity ?? 0);
    const orderQuantity = Number(vo.orderQuantity ?? 0);
    return {
      id: idx + 1,
      selected: false,
      materialNumber: vo.materialCode ?? '',
      description: vo.description ?? '',
      unitPrice: this.formatMoney(unitPriceNum),
      deliveryNumber: vo.dnNo ?? '',
      deliveryLineNumber: vo.dnItemNo ?? '',
      deliveryQty: Number(vo.dnQuantity ?? 0),
      remainingQty: remaining,
      claimQty: 0,
      amount: this.formatMoney(0),
      orderNo: vo.orderNo ?? '',
      orderItemNo: vo.orderItemNo ?? '',
      orderTotalAmount: this.formatMoney(Number(vo.orderItemTotalAmount ?? 0)),
      orderQuantity,
      dnQuantity: Number(vo.dnQuantity ?? vo.orderQuantity ?? 0),
      raw: vo,
    };
  }

  /** ClaimInvoiceItemVO → ClaimItemRow（发票模式）。
   *  发票模式下表格的「提单号 / 提单行号 / 提单行数量」字段被复用展示
   *  「发票号 / 发票行号 / 已开票数量」，避免引入第二套表头。 */
  private mapInvoiceRow(vo: any, idx: number): ClaimItemRow {
    const billedQty = Number(vo.billedQty ?? vo.invoicedQuantity ?? 0);
    const unitPriceNum =
      Number(vo.unitPriceWithTax ?? vo.unitPrice ?? 0) ||
      (billedQty > 0 ? Number(vo.netValue ?? 0) / billedQty : 0);
    const remaining = Number(vo.availableQuantity ?? 0);
    return {
      id: idx + 1,
      selected: false,
      materialNumber: vo.materialNumber ?? vo.partNumber ?? '',
      description: vo.materialName ?? vo.materialDescription ?? '',
      unitPrice: this.formatMoney(unitPriceNum, vo.currency),
      deliveryNumber: vo.referenceNo ?? '',
      deliveryLineNumber: vo.referenceItemNo ?? '',
      deliveryQty: Number(vo.dnQuantity ?? 0),
      remainingQty: remaining,
      claimQty: 0,
      amount: this.formatMoney(0, vo.currency),
      invoiceNo: vo.billingNo ?? '',
      invoiceItemNo: vo.billingItemNo ?? '',
      dnTotalValueWithTax: this.formatMoney(Number(vo.netValue ?? vo.dnTotalValueWithTax ?? 0), vo.currency),
      dnQuantity: billedQty,
      raw: vo,
    };
  }

  private hydrateCartFromReferences(references: any[]): void {
    const refs = Array.isArray(references) ? references : [];
    const cart: ClaimCartItem[] = [];

    refs.forEach((ref: any) => {
      const items = Array.isArray(ref?.items) ? ref.items : [];
      const refType = (ref?.referenceType ?? '').toString().toUpperCase();
      items.forEach((item: any, idx: number) => {
        const material = item?.materialCode ?? item?.materialNumber ?? '';
        const isOrderMode = refType === 'ORDER' || refType === 'CONSIGNMENT';
        const deliveryNumber = item?.dnNo ?? (isOrderMode
          ? ''
          : (item?.referenceNo ?? ref?.referenceNo ?? ''));
        const deliveryLineNumber = isOrderMode
          ? (item?.dnItemNo ?? '')
          : (item?.referenceItemNo ?? item?.dnItemNo ?? '');
        const claimQty = Number(item?.claimQuantity ?? 0);
        const submittableQty = Number(item?.availableQuantity ?? claimQty);
        const deliveryQty = Number(item?.dnQuantity ?? 0);
        const unitPriceNum = Number(item?.unitPriceWithTax ?? item?.unitPrice ?? 0);
        const claimAmountNum = unitPriceNum > 0
          ? claimQty * unitPriceNum
          : Number(item?.dnTotalValueWithTax ?? 0);

        cart.push({
          rowId: cart.length + idx + 1,
          materialNumber: material,
          invoiceAmount: this.formatMoney(
            Number(item?.dnTotalValueWithTax ?? item?.unitPriceWithTax ?? 0),
            item?.currency,
          ),
          orderLineQty: deliveryQty,
          deliveryNumber,
          deliveryLineNumber,
          deliveryQty,
          submittableQty,
          claimQty,
          claimAmount:
            item?.claimAmount != null && item?.claimAmount !== ''
              ? this.formatMoney(item.claimAmount, item?.currency)
              : this.formatMoney(claimAmountNum, item?.currency),
          receiverNumber: item?.receiverNumber ?? 'SH-0091',
          returnOrderNumber: item?.returnOrderNumber ?? '-',
          orderNo: item?.orderNo ?? (isOrderMode ? ref?.referenceNo ?? '' : ''),
          orderItemNo: item?.orderItemNo ?? '',
          orderTotalAmount: isOrderMode
            ? this.formatMoney(Number(item?.dnTotalValueWithTax ?? 0), item?.currency)
            : undefined,
          orderQuantity: isOrderMode
            ? Number(item?.orderQuantity ?? item?.dnQuantity ?? 0)
            : undefined,
          invoiceNo: item?.invoiceNo ?? item?.billingNo ?? '',
          invoiceItemNo: item?.invoiceItemNo ?? item?.billingItemNo ?? '',
          dnTotalValueWithTax: !isOrderMode
            ? this.formatMoney(Number(item?.netValue ?? item?.dnTotalValueWithTax ?? 0), item?.currency)
            : undefined,
          dnQuantity: Number(item?.billedQty ?? item?.invoicedQuantity ?? item?.dnQuantity ?? item?.orderQuantity ?? 0),
          priceDifference: item?.priceDifference != null ? String(item.priceDifference) : '0',
          raw: {
            ...item,
            orderNo:
              item?.orderNo ??
              (isOrderMode ? ref?.referenceNo ?? null : item?.orderNo),
            invoiceNo:
              item?.invoiceNo ??
              (!isOrderMode ? ref?.referenceNo ?? null : item?.invoiceNo),
          },
        });
      });
    });

    this.cartItems = cart;
  }

  /**
   * 编辑态：根据已填充的购物车数据构建主表格行，
   * 使主表格回显已保存的索赔数量、正确的销售单价、索赔行金额等字段。
   */
  private hydrateDataSourceFromCart(): void {
    this.dataSource = this.cartItems.map((cart, idx) => {
      const raw = cart.raw ?? {};
      const unitPriceNum = Number(raw.unitPriceWithTax ?? raw.unitPrice ?? 0);
      const currency = raw.currency;
      const isOrderMode = !!cart.orderNo;

      return {
        id: idx + 1,
        selected: true,
        materialNumber: cart.materialNumber,
        description: raw.description ?? raw.materialName ?? raw.materialDescription ?? '',
        unitPrice: this.formatMoney(unitPriceNum, currency),
        deliveryNumber: cart.deliveryNumber,
        deliveryLineNumber: cart.deliveryLineNumber,
        deliveryQty: cart.deliveryQty,
        remainingQty: cart.submittableQty,
        claimQty: cart.claimQty,
        amount: cart.claimAmount,
        orderNo: cart.orderNo ?? '',
        orderItemNo: cart.orderItemNo ?? '',
        orderTotalAmount: cart.orderTotalAmount,
        orderQuantity: isOrderMode
          ? Number(raw.orderQuantity ?? raw.dnQuantity ?? 0)
          : undefined,
        invoiceNo: cart.invoiceNo ?? '',
        invoiceItemNo: cart.invoiceItemNo ?? '',
        dnTotalValueWithTax: cart.dnTotalValueWithTax,
        dnQuantity: cart.dnQuantity,
        priceDifference: cart.priceDifference,
        raw,
      };
    });
  }

  private applyCartSelectionToDataSource(): void {
    if (!this.dataSource.length || !this.cartItems.length) {
      return;
    }

    this.dataSource.forEach(row => {
      const cart = this.findCartItemForRow(row);
      if (!cart) {
        return;
      }
      row.selected = true;
      row.claimQty = cart.claimQty;
      row.amount = this.formatMoney(
        this.extractMoneyNumber(cart.claimAmount),
        this.currencyFromMoney(row.unitPrice) ??
        this.currencyFromMoney(cart.claimAmount) ??
        undefined,
      );
      cart.rowId = row.id;
      cart.raw = row.raw;
    });
  }

  isRowInCart(row: ClaimItemRow): boolean {
    return !!this.findCartItemForRow(row);
  }

  private findCartItemForRow(row: ClaimItemRow): ClaimCartItem | undefined {
    // Primary key: deliveryNumber + deliveryLineNumber (maps to 订单号+行号 or 发票号+行号)
    return this.cartItems.find(item =>
      item.materialNumber === row.materialNumber &&
      item.deliveryNumber === row.deliveryNumber &&
      item.deliveryLineNumber === row.deliveryLineNumber,
    );
  }

  private currencyFromMoney(value: string | undefined | null): string | null {
    if (!value) {
      return null;
    }
    const parts = value.trim().split(' ');
    return parts.length ? parts[0] : null;
  }

  private extractMoneyNumber(value: string | undefined | null): number {
    if (!value) {
      return 0;
    }
    const num = Number((value || '').replace(/[^\d.-]/g, ''));
    return Number.isNaN(num) ? 0 : num;
  }

  private pickFirstString(source: any, keys: string[]): string {
    for (const key of keys) {
      const value = source?.[key];
      if (value !== null && value !== undefined && `${value}`.trim() !== '') {
        return `${value}`;
      }
    }
    return '';
  }

  private dropEmpty(obj: Record<string, any>): Record<string, any> {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([, v]) => v !== null && v !== undefined && v !== '',
      ),
    );
  }

  private formatDateParam(date: Date | string): string {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private formatMoney(value: number, currency?: string): string {
    if (Number.isNaN(value)) {
      value = 0;
    }
    const formatted = value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${currency ?? 'CNY'} ${formatted}`;
  }

  /**
   * 业务主键：materialNumber + deliveryNumber + deliveryLineNumber
   * id 只是页内行号，跨页会冲突，不能用于跨页匹配。
   */
  private rowKey(row: ClaimItemRow): string {
    return `${row.materialNumber}|${row.deliveryNumber}|${row.deliveryLineNumber}`;
  }

  handleQuickAdd(): void {
    // 合并跨页选中行 + 当前页选中行，按业务主键去重（当前页优先）
    const currentKeys = new Set(this.dataSource.map(r => this.rowKey(r)));
    const allSelected: ClaimItemRow[] = [];
    // 先加入 pending 中不在当前页的行（当前页的以最新 selected 为准）
    this.pendingSelectedRows.forEach(row => {
      if (!currentKeys.has(this.rowKey(row)) && row.selected) {
        allSelected.push(row);
      }
    });
    // 再加入当前页选中的行
    this.dataSource.filter(row => row.selected).forEach(row => allSelected.push(row));

    // 校验：选中行中存在可索赔数量为0的条目，阻止添加到购物车
    const hasZeroRemaining = allSelected.some(row => !this.isRowClaimable(row));
    if (hasZeroRemaining) {
      this.message.warning(this.translate.instant('CLAIM_CREATE_ZERO_REMAINING'));
      return;
    }

    // 校验：选中行中存在索赔数量为0的条目，阻止添加到购物车
    const hasZeroQty = allSelected.some(row => !row.claimQty || row.claimQty <= 0);
    if (hasZeroQty) {
      this.message.warning(this.translate.instant('CLAIM_CREATE_ZERO_QTY'));
      return;
    }

    // 添加选中的行到购物车
    allSelected.forEach((row) => {
      this.addRowToCart(row);
    });

    // 移除取消勾选的行：只移除本次搜索结果中不再选中的项，不影响其他来源的购物车数据
    const selectedKeys = new Set(allSelected.map(r => this.rowKey(r)));
    const allSearchKeys = new Set<string>();
    [...this.pendingSelectedRows, ...this.dataSource].forEach(row => {
      allSearchKeys.add(this.rowKey(row));
    });
    const beforeCount = this.cartItems.length;
    this.cartItems = this.cartItems.filter(item => {
      const itemKey = `${item.materialNumber}|${item.deliveryNumber}|${item.deliveryLineNumber}`;
      if (allSearchKeys.has(itemKey) && !selectedKeys.has(itemKey)) {
        return false;
      }
      return true;
    });
    const removedCount = beforeCount - this.cartItems.length;

    // 清空临时变量
    this.pendingSelectedRows = [];

    // 购物车按 订单号+订单行号（或发票号+发票行号）升序排序
    this.cartItems.sort((a, b) => {
      const aOrder = a.orderNo ?? a.invoiceNo ?? '';
      const bOrder = b.orderNo ?? b.invoiceNo ?? '';
      const aItem = a.orderItemNo ?? a.invoiceItemNo ?? '';
      const bItem = b.orderItemNo ?? b.invoiceItemNo ?? '';
      const orderCompare = aOrder.localeCompare(bOrder, undefined, { numeric: true });
      if (orderCompare !== 0) { return orderCompare; }
      return aItem.localeCompare(bItem, undefined, { numeric: true });
    });

    if (allSelected.length > 0 && removedCount > 0) {
      this.message.success(
        this.translate.instant('CLAIM_CREATE_ADDED_AND_REMOVED', {
          added: allSelected.length,
          removed: removedCount,
        }),
      );
    } else if (allSelected.length > 0) {
      this.message.success(
        this.translate.instant('CLAIM_CREATE_ADDED', {
          count: allSelected.length,
        }),
      );
    } else if (removedCount > 0) {
      this.message.success(
        this.translate.instant('CLAIM_CREATE_REMOVED', {
          count: removedCount,
        }),
      );
    } else {
      this.message.warning(this.translate.instant('CLAIM_CREATE_SELECT_FIRST'));
    }
  }

  /**
   * 翻页前把当前页选中行存入临时变量（按业务主键去重），不弹 toast。
   */
  private savePendingSelections(): void {
    const pendingKeys = new Set(this.pendingSelectedRows.map(r => this.rowKey(r)));
    this.dataSource
      .filter(row => row.selected && this.isRowClaimable(row))
      .forEach(row => {
        if (!pendingKeys.has(this.rowKey(row))) {
          this.pendingSelectedRows.push(row);
        }
      });
  }

  /** 购物车「保存」—— 把购物车里的索赔数量、正确的销售单价同步回 dataSource，并更新 claimAmount。 */
  saveCartChanges(): void {
    this.cartItems.forEach(item => {
      const key = this.cartItemKey(item);
      const row = this.dataSource.find(r => this.rowKey(r) === key);
      if (row) {
        row.claimQty = item.claimQty;
        row.amount = item.claimAmount;
        row.priceDifference = item.priceDifference;
      }
    });
    this.message.success(this.translate.instant('CLAIM_CREATE_QTY_SAVED'));
    this.cartOpen = false;
  }

  private cartItemKey(item: ClaimCartItem): string {
    return `${item.materialNumber}|${item.deliveryNumber}|${item.deliveryLineNumber}`;
  }

  trackCartItem = (_index: number, item: ClaimCartItem): string => {
    return this.cartItemKey(item);
  };

  /** 购物车删除后，同步取消外层列表（含跨页 pending）中对应行的选中状态。 */
  private deselectRowsByCartKeys(keys: string[]): void {
    if (!keys.length) {
      return;
    }
    const keySet = new Set(keys);
    this.dataSource.forEach(row => {
      if (keySet.has(this.rowKey(row))) {
        row.selected = false;
      }
    });
    this.pendingSelectedRows = this.pendingSelectedRows.filter(row => {
      if (keySet.has(this.rowKey(row))) {
        row.selected = false;
        return false;
      }
      return true;
    });
  }

  addRowToCart(row: ClaimItemRow): void {
    const key = this.rowKey(row);
    const existing = this.cartItems.find((c) => this.cartItemKey(c) === key);
    if (existing) {
      existing.claimQty = row.claimQty;
      existing.claimAmount = row.amount;
      existing.priceDifference = row.priceDifference;
      existing.rowId = row.id;
      return;
    }
    this.cartItems.push({
      rowId: row.id,
      materialNumber: row.materialNumber,
      invoiceAmount: row.unitPrice,
      orderLineQty: row.deliveryQty,
      deliveryNumber: row.deliveryNumber,
      deliveryLineNumber: row.deliveryLineNumber,
      raw: row.raw,
      deliveryQty: row.deliveryQty,
      submittableQty: row.remainingQty,
      claimQty: row.claimQty,
      claimAmount: row.amount,
      receiverNumber: 'SH-0091',
      returnOrderNumber: '-',
      orderNo: row.orderNo,
      orderItemNo: row.orderItemNo,
      orderTotalAmount: row.orderTotalAmount,
      orderQuantity: row.orderQuantity,
      invoiceNo: row.invoiceNo,
      invoiceItemNo: row.invoiceItemNo,
      dnTotalValueWithTax: row.dnTotalValueWithTax,
      dnQuantity: row.dnQuantity,
      priceDifference: row.priceDifference,
    });
  }

  toggleCart(): void {
    this.cartOpen = !this.cartOpen;
  }

  closeCart(): void {
    this.cartOpen = false;
  }

  decreaseCartQty(item: ClaimCartItem): void {
    if (item.claimQty > 0) {
      item.claimQty -= 1;
      this.recalcCartItemAmount(item);
    }
  }

  increaseCartQty(item: ClaimCartItem): void {
    if (item.claimQty < item.submittableQty) {
      item.claimQty += 1;
      this.recalcCartItemAmount(item);
    }
  }

  handlePriceDifferenceInput(row: ClaimItemRow, value: string, inputEl?: HTMLInputElement): void {
    const num = Number(value);
    if (!isNaN(num) && num >= 0) {
      row.priceDifference = value;
    }
    this.recalcRowAmount(row);
  }

  handlePriceDifferenceBlur(row: ClaimItemRow, value: string, inputEl?: HTMLInputElement): void {
    const num = Number(value);
    if (value === '' || isNaN(num) || num < 0) {
      row.priceDifference = '0';
      if (inputEl) { inputEl.value = '0'; }
      this.recalcRowAmount(row);
    }
  }

  handleCartPriceDifferenceInput(item: ClaimCartItem, value: string, inputEl?: HTMLInputElement): void {
    const num = Number(value);
    if (!isNaN(num) && num >= 0) {
      item.priceDifference = value;
    }
    this.recalcCartItemAmount(item);
  }

  handleCartPriceDifferenceBlur(item: ClaimCartItem, value: string, inputEl?: HTMLInputElement): void {
    const num = Number(value);
    if (value === '' || isNaN(num) || num < 0) {
      item.priceDifference = '0';
      if (inputEl) { inputEl.value = '0'; }
      this.recalcCartItemAmount(item);
    }
  }

  handleCartQtyInput(item: ClaimCartItem, value: any): void {
    const num = Number(value);
    if (!isNaN(num)) {
      if (num <= 0) {
        this.removeCartItem(item);
      } else {
        item.claimQty = Math.min(num, item.submittableQty);
        this.recalcCartItemAmount(item);
      }
    }
  }

  /**
   * 购物车中索赔行金额重算：
   * - 价格差异模式：（发票行总金额（不含税）/ 发票行数量 - 正确的销售单价（不含税））* 索赔数量
   * - 发票模式：发票行总金额（含税）* 索赔数量 / 发票行数量(billedQty)
   * - 订单模式：订单行总金额（含税）* 索赔数量 / 订单行数量(orderQuantity)
   */
  private recalcCartItemAmount(item: ClaimCartItem): void {
    let amount = 0;
    if (this.isPriceDifferenceMode) {
      const priceDiff = Number(item.priceDifference) || 0;
      if (this.isInvoiceMode) {
        const totalAmount = this.extractMoneyNumber(item.dnTotalValueWithTax);
        const dnQuantity = item.dnQuantity ?? 0;
        amount = dnQuantity > 0 ? (totalAmount / dnQuantity - priceDiff) * item.claimQty : 0;
      } else {
        const totalAmount = this.extractMoneyNumber(item.orderTotalAmount);
        const orderQty = item.orderQuantity ?? 0;
        amount = orderQty > 0 ? (totalAmount / orderQty - priceDiff) * item.claimQty : 0;
      }
    } else if (this.isInvoiceMode) {
      const totalAmount = this.extractMoneyNumber(item.dnTotalValueWithTax);
      const dnQuantity = item.dnQuantity ?? 0;
      amount = dnQuantity > 0 ? totalAmount * item.claimQty / dnQuantity : 0;
    } else {
      const totalAmount = this.extractMoneyNumber(item.orderTotalAmount);
      const orderQty = item.orderQuantity ?? 0;
      amount = orderQty > 0 ? totalAmount * item.claimQty / orderQty : 0;
    }
    const currency = this.currencyFromMoney(item.invoiceAmount) ?? undefined;
    item.claimAmount = this.formatMoney(amount, currency);
  }

  removeCartItem(item: ClaimCartItem): void {
    const key = this.cartItemKey(item);
    this.cartItems = this.cartItems.filter((c) => this.cartItemKey(c) !== key);
    this.deselectRowsByCartKeys([key]);
  }

  handleClearCart(): void {
    if (this.cartItems.length === 0) {
      this.message.warning(this.translate.instant('CLAIM_CREATE_CART_EMPTY'));
      return;
    }
    const keys = this.cartItems.map(item => this.cartItemKey(item));
    this.cartItems = [];
    this.deselectRowsByCartKeys(keys);
    this.message.success(this.translate.instant('CLAIM_CREATE_CART_CLEARED'));
  }

  pageIndexChange(pageIndex: number): void {
    if (pageIndex === this.pagination.pageIndex) {
      return;
    }
    // 翻页前把当前页选中的记录存入临时变量，支持跨页选择
    this.savePendingSelections();
    this.pagination.pageIndex = pageIndex;
    this.handleSearchOrder();
  }

  /**
   * 触发隐藏 <input type="file"> 弹出系统文件选择框。
   * 真正的上传在 `onAttachmentSelected` 里完成。
   */
  handleUpload(): void {
    const input: HTMLInputElement | null = this.elementRef.nativeElement
      .querySelector('input[data-role="attachment-input"]');
    if (input) {
      input.value = '';
      input.click();
    }
  }

  /**
   * 选好文件后逐个调用 `/claimAttachment/upload`，成功后把
   * attachmentId + fileName 落到 `attachments`，失败提示并跳过。
   */
  private readonly MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) {
      return;
    }
    const oversized = files.filter(f => f.size > this.MAX_FILE_SIZE);
    if (oversized.length > 0) {
      this.message.error(
        this.translate.instant('CLAIM_CREATE_FILE_OVERSIZED', {
          files: oversized.map(f => f.name).join('、'),
        }),
      );
    }
    files.filter(f => f.size <= this.MAX_FILE_SIZE).forEach(file => this.uploadOne(file));
    input.value = '';
  }

  private uploadOne(file: File): void {
    this.attachmentUploading = true;
    this.claimService.apiClaimAttachmentUpload(file).subscribe({
      next: (result: any) => {
        this.attachmentUploading = false;
        const vo = result?.rtnData;
        if (!vo?.attachmentId) {
          this.message.error(
            this.translate.instant('CLAIM_CREATE_UPLOAD_FAIL', {
              name: file.name,
            }),
          );
          return;
        }
        this.attachments = [
          ...this.attachments,
          {
            attachmentId: vo.attachmentId,
            fileName: vo.fileName ?? file.name,
            size: file.size,
          },
        ];
        this.message.success(
          this.translate.instant('CLAIM_CREATE_UPLOAD_SUCCESS', {
            name: vo.fileName ?? file.name,
          }),
        );
      },
      error: () => {
        this.attachmentUploading = false;
        this.message.error(
          this.translate.instant('CLAIM_CREATE_UPLOAD_FAIL', {
            name: file.name,
          }),
        );
      },
    });
  }

  /**
   * 下载已上传的附件 — 后端以二进制流写响应，因此这里把 blob
   * 包装成临时 <a> 触发浏览器下载。文件名优先用前端记录的
   * name；若 Content-Disposition 给出 filename 则覆盖之。
   */
  downloadAttachment(att: { attachmentId: string; fileName: string }): void {
    this.claimService
      .apiClaimAttachmentDownload(att.attachmentId, att.fileName)
      .subscribe({
        next: response => {
          const blob = response.body;
          if (!blob) {
            this.message.error(this.translate.instant('CLAIM_DETAIL_DOWNLOAD_EMPTY'));
            return;
          }
          const fileName =
            this.parseFilenameFromDisposition(
              response.headers.get('content-disposition'),
            ) ?? att.fileName;
          this.triggerBrowserDownload(blob, fileName);
        },
        error: () =>
          this.message.error(this.translate.instant('CLAIM_DETAIL_DOWNLOAD_FAIL')),
      });
  }

  removeAttachment(att: { attachmentId: string; fileName: string }): void {
    this.claimService.apiClaimAttachmentDelete(att.attachmentId).subscribe({
      next: () => {
        this.attachments = this.attachments.filter(
          a => a.attachmentId !== att.attachmentId,
        );
      },
      error: () =>
        this.message.error(this.translate.instant('CLAIM_CREATE_DELETE_ATTACHMENT_FAIL')),
    });
  }

  /** 从 Content-Disposition 解析 RFC 5987 / 普通 filename。 */
  private parseFilenameFromDisposition(header: string | null): string | null {
    if (!header) {
      return null;
    }
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
    if (utf8?.[1]) {
      try {
        return decodeURIComponent(utf8[1].trim().replace(/^"|"$/g, ''));
      } catch {
        return utf8[1];
      }
    }
    const plain = /filename="?([^";]+)"?/i.exec(header);
    return plain?.[1] ?? null;
  }

  private triggerBrowserDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  handleDownloadGuide(): void {
    const fileName = '物流索赔平台上传附件规范.pdf';
    fetch(`assets/claim/${encodeURIComponent(fileName)}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('download failed');
        }
        return response.blob();
      })
      .then(blob => this.triggerBrowserDownload(blob, fileName))
      .catch(() =>
        this.message.error(this.translate.instant('CLAIM_CREATE_DOWNLOAD_SPEC_FAIL')));
  }

  // 防重复点击。
  submitting = false;
  savingDraft = false;

  /**
   * 「保存 / 更新」按钮 — 对应后端 `POST /claim/submit`。
   * 提交时后端会校验 cart 必填 + 推送 BSP，因此前端先做轻量预校验
   * 再发请求，避免无意义的 4xx 往返。
   */
  handleSave(): void {
    if (this.submitting) {
      return;
    }
    const validationError = this.validateForSubmit();
    if (validationError) {
      this.message.warning(validationError);
      return;
    }
    this.submitting = true;
    this.claimService.apiClaimSubmit(this.buildSavePayload()).subscribe({
      next: (result: any) => {
        this.submitting = false;
        if (result?.rtnCode === 10000) {
          this.message.success(this.translate.instant('CLAIM_CREATE_SUBMIT_SUCCESS'));
          this.navigateBackToOverview();
        } else {
          this.message.error(
            result?.rtnMsg ?? this.translate.instant('CLAIM_CREATE_SUBMIT_FAIL'),
          );
        }
      },
      error: (err: any) => {
        this.submitting = false;
        this.message.error(
          err?.error?.rtnMsg ?? this.translate.instant('CLAIM_CREATE_SUBMIT_FAIL'),
        );
      },
    });
  }

  /**
   * 「保存为草稿」按钮 — 对应后端 `POST /claim/saveDraft`。仅在新建模式
   * 显示；后端校验宽松（只要 referType 非空即可），无 BSP 调用。
   */
  handleSaveDraft(): void {
    if (this.savingDraft) {
      return;
    }
    this.savingDraft = true;
    this.claimService.apiClaimSaveDraft(this.buildSavePayload()).subscribe({
      next: (result: any) => {
        this.savingDraft = false;
        if (result?.rtnCode === 10000) {
          this.message.success(this.translate.instant('CLAIM_CREATE_DRAFT_SAVED'));
          this.navigateBackToOverview();
        } else {
          this.message.error(
            result?.rtnMsg ?? this.translate.instant('CLAIM_CREATE_DRAFT_SAVE_FAIL'),
          );
        }
      },
      error: (err: any) => {
        this.savingDraft = false;
        this.message.error(
          err?.error?.rtnMsg ?? this.translate.instant('CLAIM_CREATE_DRAFT_SAVE_FAIL'),
        );
      },
    });
  }

  /**
   * 组装 `ClaimSaveReqVO` 请求体（草稿 / 提交共用）。
   *
   * 关键映射：
   * - `referType`: 寄售(consignment) → ORDER；非寄售(normal) → INVOICE
   * - `references`: 按 orderNo / invoiceNo 把 `cartItems` 分组聚合
   * - `attachmentIds`: 已上传到 BSP 的附件 ID 透传
   */
  private buildSavePayload(): any {
    const basic = this.basicForm.value;
    const other = this.otherForm.value;
    const referType = this.isInvoiceMode ? 'invoice' : 'order';

    return {
      id: this.isEditMode && this.editingClaimId
        ? Number(this.editingClaimId)
        : undefined,
      referType,
      claimType: basic.reason ?? null,
      claimCategory: basic.category ?? null,
      ruleLevel1: basic.referenceType === 'normal' ? 'Normal' : 'Consignment',
      ruleLevel2: basic.scheme ?? null,
      selfReturn: this.showSelfReturnSection ? (basic.selfReturn === 'yes' ? 1 : 0) : 0,
      contactAddress: (this.showSelfReturnSection && basic.selfReturn === 'no') ? (basic.customerAddress ?? null) : null,
      contactName: (this.showSelfReturnSection && basic.selfReturn === 'no') ? (basic.customerContact ?? null) : null,
      contactPhone: (this.showSelfReturnSection && basic.selfReturn === 'no') ? (basic.customerPhone ?? null) : null,
      comments: other.remark ?? '',
      currency: 'CNY',
      references: this.buildReferences(referType),
      attachments: this.attachments.map(a => ({ attachmentId: a.attachmentId, fileName: a.fileName })),
    };
  }

  /**
   * 按 ORDER/INVOICE 模式分组 `cartItems`：
   * - ORDER 模式：按 `raw.orderNo` 聚合
   * - INVOICE 模式：按 `raw.invoiceNo`（fallback 到 cartItem.deliveryNumber）
   */
  private buildReferences(referType: 'invoice' | 'order'): any[] {
    const groups = new Map<string, any[]>();
    this.cartItems.forEach(item => {
      const referenceNo = this.referenceNoOf(item, referType);
      if (!referenceNo) {
        return;
      }
      if (!groups.has(referenceNo)) {
        groups.set(referenceNo, []);
      }
      groups.get(referenceNo)!.push(this.cartItemToReqItem(item, referType));
    });
    return Array.from(groups.entries()).map(([referenceNo, items]) => ({
      referenceType: referType,
      referenceNo,
      items,
    }));
  }

  private referenceNoOf(
    item: ClaimCartItem,
    referType: 'invoice' | 'order',
  ): string {
    if (referType === 'order') {
      return item.raw?.orderNo ?? '';
    }
    return item.raw?.billingNo ?? item.raw?.invoiceNo ?? item.deliveryNumber ?? '';
  }

  private cartItemToReqItem(
    item: ClaimCartItem,
    referType: 'invoice' | 'order',
  ): any {
    const raw = item.raw ?? {};
    const base: any = {
      materialCode: item.materialNumber || raw.materialCode || raw.materialNumber,
      description: raw.description ?? raw.materialName ?? raw.materialDescription ?? '',
      availableQuantity: item.submittableQty,
      claimQuantity: item.claimQty,
      claimAmount: this.extractMoneyNumber(item.claimAmount),
    };
    if (referType === 'order') {
      return {
        ...base,
        orderNo: raw.orderNo ?? '',
        orderItemNo: raw.orderItemNo ?? '',
        dnNo: raw.dnNo ?? item.deliveryNumber,
        dnItemNo: raw.dnItemNo ?? item.deliveryLineNumber,
        dnQuantity: Number(raw.dnQuantity ?? item.deliveryQty),
        unitPrice: raw.unitPrice ?? null,
        unitPriceWithTax: raw.unitPriceWithTax ?? null,
        dnTotalValueWithTax: raw.dnTotalValueWithTax ?? raw.orderItemTotalAmount ?? null,
      };
    }
    return {
      ...base,
      dnNo: item.deliveryNumber || raw.dnNo || '',
      dnQuantity: Number(raw.dnQuantity ?? item.deliveryQty),
      dnItemNo: raw.dnItemNo ?? item.deliveryLineNumber,
      dnTotalValueWithTax: raw.dnTotalValueWithTax ?? raw.netValue ?? null,
      invoiceNo: raw.billingNo ?? raw.invoiceNo ?? item.deliveryNumber,
      invoiceItemNo: raw.billingItemNo ?? raw.invoiceItemNo ?? item.deliveryLineNumber,
      orderNo: raw.orderNo ?? null,
      priceDifference: item.priceDifference != null ? Number(item.priceDifference) : null,
    };
  }

  /**
   * 提交前预校验。saveDraft 跳过本方法（后端只校验 referType 非空）。
   * 返回 null 表示通过，否则返回 toast 文案。
   */
  private validateForSubmit(): string | null {
    const basic = this.basicForm.value;
    if (!basic.referenceType) {
      return this.translate.instant('CLAIM_VALIDATE_REFERENCE_TYPE');
    }
    if (!basic.reason) {
      return this.translate.instant('CLAIM_VALIDATE_REASON');
    }
    if (!basic.category) {
      return this.translate.instant('CLAIM_VALIDATE_CATEGORY');
    }
    if (!basic.scheme) {
      return this.translate.instant('CLAIM_VALIDATE_SCHEME');
    }
    if (this.showSelfReturnSection) {
      if (!basic.selfReturn) {
        return this.translate.instant('CLAIM_VALIDATE_SELF_RETURN');
      }
      if (basic.selfReturn === 'no') {
        if (!basic.customerAddress) {
          return this.translate.instant('CLAIM_VALIDATE_CUSTOMER_ADDRESS');
        }
        if (!basic.customerContact) {
          return this.translate.instant('CLAIM_VALIDATE_CUSTOMER_CONTACT');
        }
        if (!basic.customerPhone) {
          return this.translate.instant('CLAIM_VALIDATE_CUSTOMER_PHONE');
        }
      }
    }
    if (!this.cartItems.length) {
      return this.translate.instant('CLAIM_VALIDATE_CART_EMPTY');
    }
    const zeroRemaining = this.cartItems.find(c => !this.isCartItemClaimable(c));
    if (zeroRemaining) {
      return this.translate.instant('CLAIM_VALIDATE_ZERO_REMAINING', {
        material: zeroRemaining.materialNumber,
      });
    }
    const invalid = this.cartItems.find(c => !c.claimQty || c.claimQty <= 0);
    if (invalid) {
      return this.translate.instant('CLAIM_VALIDATE_INVALID_QTY', {
        material: invalid.materialNumber,
      });
    }
    if (this.isPriceDifferenceMode) {
      const invalidPrice = this.cartItems.find(c => {
        const pd = Number(c.priceDifference);
        return isNaN(pd) || pd < 0;
      });
      if (invalidPrice) {
        return this.translate.instant('CLAIM_VALIDATE_INVALID_PRICE', {
          material: invalidPrice.materialNumber,
        });
      }
    }
    const invalidAmount = this.cartItems.find(c => {
      const amt = this.extractMoneyNumber(c.claimAmount);
      return isNaN(amt) || amt <= 0;
    });
    if (invalidAmount) {
      return this.translate.instant('CLAIM_VALIDATE_INVALID_AMOUNT', {
        material: invalidAmount.materialNumber,
      });
    }
    if (!this.attachments.length) {
      return this.translate.instant('CLAIM_VALIDATE_ATTACHMENT');
    }
    return null;
  }

  private navigateBackToOverview(): void {
    const country =
      this.route.snapshot.paramMap.get('country') ||
      localStorage.getItem('country') ||
      'iam';
    this.router.navigate([country, 'claim']);
  }

  handleCancel(): void {
    const country =
      this.route.snapshot.paramMap.get('country') ||
      localStorage.getItem('country') ||
      'iam';
    this.router.navigate([country, 'claim']);
  }

  /** 非寄售 + 索赔原因=价格差异 + 索赔分类=价格差异 时显示「正确的销售单价」列 */
  get isPriceDifferenceMode(): boolean {
    if (this.basicForm?.get('referenceType')?.value !== 'normal') return false;
    const reason = this.basicForm?.get('reason')?.value;
    const category = this.basicForm?.get('category')?.value;
    const reasonOpt = this.reasonOptions.find(o => o.value === reason);
    const categoryOpt = this.categoryOptions.find(o => o.value === category);
    return (reasonOpt?.label ?? reason) === '价格差异'
      && (categoryOpt?.label ?? category) === '价格差异';
  }

  /**
   * 索赔方式为 Return for Credit / Exchange / Return 时才展示
   * 「是否自行寄回」区块及其关联的客户取件信息。
   */
  get showSelfReturnSection(): boolean {
    const schemeValue = this.basicForm?.get('scheme')?.value;
    if (!schemeValue) {
      return false;
    }
    const allowed = ['Return for Credit', 'Exchange', 'Return'];
    const matched = this.schemeOptions.find(opt => opt.value === schemeValue);
    const label = (matched?.label ?? '').toString().trim();
    return allowed.includes(label) || allowed.includes(schemeValue.toString().trim());
  }

  get warehouseInfoText(): string {
    return this.translate.instant('CLAIM_CREATE_WAREHOUSE_INFO');
  }

  copyWarehouseInfo(): void {
    const text = this.warehouseInfoText;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        this.message.success(this.translate.instant('CLAIM_MSG_COPY_SUCCESS'));
      }).catch(() => {
        this.fallbackCopy(text);
        this.message.success(this.translate.instant('CLAIM_MSG_COPY_SUCCESS'));
      });
    } else {
      this.fallbackCopy(text);
      this.message.success(this.translate.instant('CLAIM_MSG_COPY_SUCCESS'));
    }
  }

  private fallbackCopy(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (e) { }
    document.body.removeChild(textarea);
  }
}
